import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { filter, includes, isEmpty, map } from 'es-toolkit/compat';
import React, { useId } from 'react';

import { ResolvedSlide, SlideRunResult, WorkflowResultStatus } from 'services/export/workflowResultsSelection';

const statusLabels: Record<WorkflowResultStatus, string> = {
  published: 'Published',
  approved: 'Approved',
  notApproved: 'Not approved',
};

// The status is the chip's text, so it never relies on colour alone. A result that is neither published nor
// internally approved has no chip; the run name already identifies it.
export const ResultStatusChip: React.FC<{ status: WorkflowResultStatus }> = ({ status }) =>
  status === 'notApproved' ? null : <Chip size="small" variant="outlined" label={statusLabels[status]} />;

// The newest result on the slide, matching the case viewer's heatmaps and features, where the first newest-first
// result is marked Latest. Results here are already stored newest first.
export const isLatestSlideResult = (results: SlideRunResult[], runId: string) => results[0]?.runId === runId;

export const LatestResultChip: React.FC = () => (
  <Chip size="small" variant="outlined" color="primary" label="Latest result" />
);

const USE_PANEL_CHOICE = '__use-panel-choice__';

// The runs a slide's choice stands for: the run itself, the run that is its published or approved result, or its latest.
export const getDisplayedRunIds = ({ picks, slide }: ResolvedSlide) =>
  map(
    filter(slide.results, (result) =>
      picks.some(
        (pick) =>
          (pick.source === 'run' && pick.runId === result.runId) ||
          (pick.source === 'published' && result.isPublished) ||
          (pick.source === 'approved' && result.isInternallyApproved)
      )
    ),
    'runId'
  );

const describeChosenResults = (runIds: string[], getRunLabel: (runId: string) => string) =>
  runIds.length === 0
    ? 'No result chosen'
    : runIds.length === 1
    ? getRunLabel(runIds[0])
    : `${runIds.length} results chosen`;

interface WorkflowResultsSlideRowProps {
  resolved: ResolvedSlide;
  canChooseResults: boolean;
  // Job names are still loading. Do not show the menu, and do not fall back to the read-only published caption.
  runNamesLoading?: boolean;
  getRunLabel: (runId: string) => string;
  onToggle: (slideId: string, isSelected: boolean) => void;
  onChooseResults: (slideId: string, runIds?: string[]) => void;
  // The slide's own content, for example its cell tables.
  children?: React.ReactNode;
}

export const WorkflowResultsSlideRow: React.FC<WorkflowResultsSlideRowProps> = ({
  resolved,
  canChooseResults,
  runNamesLoading = false,
  getRunLabel,
  onToggle,
  onChooseResults,
  children,
}) => {
  const { slide, hasResult, isSelected, reason, alsoInPanelNames, hasOwnChoice } = resolved;
  const displayedRunIds = getDisplayedRunIds(resolved);
  const resultsFor = `Results for ${slide.label}`;
  const slideIdCaptionId = useId();
  const resultOf = (runId: string): SlideRunResult | undefined =>
    slide.results.find((result) => result.runId === runId);

  return (
    <Stack spacing={0.5} sx={{ py: 0.75 }} data-slide-id={slide.slideId}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
        <FormControlLabel
          sx={{ minWidth: 0, m: 0, alignItems: 'flex-start' }}
          control={
            <Checkbox
              size="small"
              sx={{ py: 0.25 }}
              checked={isSelected}
              disabled={!hasResult}
              onChange={(event) => onToggle(slide.slideId, event.target.checked)}
              inputProps={{ 'aria-label': `Select ${slide.label}`, 'aria-describedby': slideIdCaptionId }}
            />
          }
          label={
            <Stack component="span" spacing={0} sx={{ minWidth: 0 }}>
              <Typography variant="body2" component="span">
                <strong>{slide.label}</strong>
                {!isEmpty(alsoInPanelNames) && (
                  <Typography variant="caption" color="text.secondary" component="span">
                    {' '}
                    / Also listed in {alsoInPanelNames.join(', ')}; listed under each panel, not merged
                  </Typography>
                )}
              </Typography>
              <Typography id={slideIdCaptionId} variant="caption" color="text.secondary" component="span">
                Slide {slide.slideId}
              </Typography>
            </Stack>
          }
        />
        {runNamesLoading ? null : canChooseResults ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {hasResult && displayedRunIds.length === 1 && resultOf(displayedRunIds[0]) && (
              <ResultStatusChip status={(resultOf(displayedRunIds[0]) as SlideRunResult).status} />
            )}
            {hasResult && displayedRunIds.length === 1 && isLatestSlideResult(slide.results, displayedRunIds[0]) && (
              <LatestResultChip />
            )}
            <Select
              size="small"
              multiple
              displayEmpty
              value={displayedRunIds}
              disabled={isEmpty(slide.results)}
              SelectDisplayProps={{ 'aria-label': resultsFor }}
              renderValue={(runIds) => describeChosenResults(runIds as string[], getRunLabel)}
              onChange={(event) => {
                const value = event.target.value;
                const chosen = (typeof value === 'string' ? value.split(',') : value) as string[];
                if (includes(chosen, USE_PANEL_CHOICE)) return onChooseResults(slide.slideId, undefined);
                // Keep the results in the order they are listed, whatever order they were picked in.
                return onChooseResults(
                  slide.slideId,
                  map(
                    filter(slide.results, (result) => includes(chosen, result.runId)),
                    'runId'
                  )
                );
              }}
              sx={{ minWidth: 240, maxWidth: 360 }}
            >
              {hasOwnChoice && (
                <MenuItem value={USE_PANEL_CHOICE} divider>
                  Use the panel&apos;s choice
                </MenuItem>
              )}
              {map(slide.results, (result) => (
                <MenuItem key={result.runId} value={result.runId}>
                  <Checkbox edge="start" size="small" checked={includes(displayedRunIds, result.runId)} tabIndex={-1} />
                  <ListItemText primary={getRunLabel(result.runId)} />
                  <ResultStatusChip status={result.status} />
                  {isLatestSlideResult(slide.results, result.runId) && <LatestResultChip />}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        ) : (
          hasResult && (
            <Typography variant="caption" color="text.secondary">
              Published results
            </Typography>
          )
        )}
      </Stack>
      {!hasResult && reason && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
          {reason}
        </Typography>
      )}
      {children}
    </Stack>
  );
};

export const SlideRowResetButton: React.FC<{ count: number; onReset: () => void }> = ({ count, onReset }) => (
  <Stack direction="row" spacing={1} alignItems="center" role="status">
    <Typography variant="body2">
      {count === 1 ? '1 slide uses' : `${count} slides use`} a different result from the choice above.
    </Typography>
    <Button size="small" color="inherit" onClick={onReset}>
      Use this choice for all slides
    </Button>
  </Stack>
);
